import { useSession as useNextSession } from "next-auth/react";
import { getMyRole } from "pages/api-clients/roles";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import INFO from "src/VERSION-INFO.json";
import { ToasterMessageType, useQueryToasterErrors } from "./useToaster";
import { Org, useOrg } from "./useOrg";
import { useSidebar } from "./useSidebar";
import { useSessionStorage } from "usehooks-ts";
import { useRouter } from "next/router";
import { createId } from "src/utils/strings";
import { GlobalFunction, globalFunction } from "pages/[orgId]/global-functions";
import { useDocumentTitle } from "./useDocumentTitle";
import { getVersion } from "pages/api-clients/version";
import { getMySubscription } from "pages/api-clients/subscriptions";
import type { Subscriptions } from "src/models";

// The application context is split into two main themes
//
// - Auth information
//   - org_id, org_name: Verified org access
//   - user, role, isOrgAdmin: Some permissions info
//   Sets the `ready` flag to true after the required API call
//
// - Global application state
//   - browser page title and setter
//   - tabs state (uses title)
//   - sidebar state

export function useApplicationContext() {
  return useContext(ApplicationContext);
}

export function useApplicationRuntime() {
  const auth = useApplicationAuth();
  const ux = useApplicationUx();
  const status = useApplicationStatus();
  // Memoize the context value
  return useMemo(() => ({ ...ux, ...status, ...auth }), [ux, status, auth]);
}

// internal hook for the global UX state
function useApplicationUx() {
  const router = useRouter();
  const { asPath, query } = router;
  const { orgId } = query;
  const [lastTitleJson, setTransitioningTitleJson] = useSessionStorage(
    `${orgId}-last-title`,
    '""'
  );
  const setTransitioningTitle = useCallback(
    (title: string) => setTransitioningTitleJson(JSON.stringify(title)),
    [setTransitioningTitleJson]
  );
  const lastTitle = JSON.parse(lastTitleJson || '""') || "";
  const [title, setTitle] = useState(lastTitle);

  const [startId] = useState(createId());
  const sidebar = useSidebar(`${orgId}-app-sidebar`, true);
  const [tabsData, setTabsData] = useSessionStorage(`${orgId}-app-tabs`, "[]");
  const tabs: AppTab[] = useMemo(() => {
    const tabs: AppTab[] = [];
    try {
      tabs.push(...JSON.parse(tabsData));
    } catch {}
    return tabs;
  }, [tabsData]);

  const crudTab = useCallback(
    (tab: Partial<AppTab>, action: "add" | "remove" | "merge") => {
      setTabsData(
        JSON.stringify(
          [
            ...tabs.map((tab_) => {
              if (tab_.id !== tab.id) return tab_;
              if (action === "merge") return { ...tab_, ...tab };
              return undefined;
            }),
            action === "add" ? (tab as AppTab) : undefined,
          ].filter(Boolean)
        )
      );
    },
    [tabs, setTabsData]
  );

  const isSessionable = useIsSessionable();
  const formattedTitle = title
    ?.replace(/ - vzbl$/i, "")
    .replace(/ \(\w+\)$/, "");

  const [activeId, setActiveId] = useSessionStorage(
    `${orgId}-app-tabs-active-id`,
    startId
  );

  // when there are no tabs, and the user is on a sessionable page,
  // then we'll create a tab and use it for them.
  useEffect(() => {
    if (formattedTitle && tabs.length === 0 && isSessionable) {
      crudTab(
        {
          id: startId,
          start_title: formattedTitle,
          current_title: formattedTitle,
          start_url: asPath,
          current_url: asPath,
        },
        "add"
      );
      if (activeId !== startId) {
        setActiveId(startId);
      }
    }
  }, [
    tabs,
    crudTab,
    asPath,
    startId,
    formattedTitle,
    isSessionable,
    activeId,
    setActiveId,
  ]);

  // This is the effect that updates the current url and title when they change
  useEffect(() => {
    if (isSessionable) {
      const active = tabs.find(({ id }) => id === activeId);
      if (
        active &&
        formattedTitle &&
        (active.current_url !== asPath ||
          active.current_title !== formattedTitle)
      ) {
        crudTab(
          {
            id: activeId,
            current_url: asPath,
            current_title: formattedTitle,
          },
          "merge"
        );
        setTransitioningTitle(formattedTitle);
      }
    }
  }, [
    isSessionable,
    asPath,
    formattedTitle,
    activeId,
    crudTab,
    tabs,
    setTransitioningTitle,
  ]);

  // This effect watches for the empty edge case when there are tabs, but no active tab.
  useEffect(() => {
    const active = tabs.find(({ id }) => id === activeId);
    if (!active && tabs.length > 0) setActiveId(tabs[0].id);
  }, [activeId, tabs, setActiveId]);

  useDocumentTitle(isSessionable ? formattedTitle : "");

  const meta = useMemo(() => {
    const lookup: Record<string, TabLink> = {};
    tabs.forEach(
      (tab) =>
        (lookup[tab.id] = getSessionableType(tab.current_url, orgId as string)!)
    );
    return { tabSessionTypes: lookup };
  }, [orgId, tabs]);

  const appendTab = useCallback(
    (
      title: string,
      url: string,
      options?: Partial<{ open: boolean | "replace" }>
    ) => {
      const newId = createId();
      crudTab(
        {
          current_title: title,
          current_url: url,
          id: newId,
          start_title: title,
          start_url: url,
        },
        "add"
      );
      if (options?.open) {
        setTimeout(() => {
          router[options.open === "replace" ? "replace" : "push"]({
            pathname: "/[orgId]/t/[tabId]",
            query: { orgId, tabId: newId },
          });
        }, 0);
      }
    },
    [crudTab, router, orgId]
  );

  const removeTab = useCallback(
    (id: string) => {
      if (id === activeId) return;
      const tab = tabs.find((tab) => tab.id === id);
      if (!tab) return;
      crudTab({ id }, "remove");
      const toaster = globalFunction(GlobalFunction.getToasterContext);
      if (toaster)
        toaster().addMessage(ToasterMessageType.UNDO, {
          message: `Removed "${tab.current_title}"`,
          undoType: "restore-tab",
          undoData: tab,
        });
    },
    [crudTab, activeId, tabs]
  );

  const findNewActiveId = useCallback(
    (from: string) => {
      if (tabs.length < 2) return "";
      const index = tabs.findIndex((tab) => tab.id === from);
      return tabs.at(index === 0 ? 1 : Math.max(0, index - 1))?.id || "";
    },
    [tabs]
  );

  const tab = useMemo(
    () => tabs.find((tab) => tab.id === activeId),
    [tabs, activeId]
  );

  return {
    activeTabId: activeId,
    appendTab,
    findNewActiveId,
    meta,
    removeTab,
    setTitle,
    sidebar,
    tab,
    tabs,
    title,
    setTransitioningTitle,
    setActiveId,
  };
} // end useApplicationUx

// internal hook for application status
function useApplicationStatus() {
  const query = useQuery(["app-version"], async () => getVersion());
  return {
    refreshRequired: query.isFetched && query.data !== INFO.VERSION,
  };
}

// internal hook for authy concerns
function useApplicationAuth() {
  const session = useNextSession();
  const org = useOrg();
  const org_id = org.id;
  const user = session.data?.user;
  const query = useQuery(["role", org_id], () => getMyRole(org_id!), {
    enabled: !!org_id,
  });
  const subscriptionQuery = useQuery(["subscription"], getMySubscription, {
    enabled: !!user?.id,
  });
  useQueryToasterErrors(query);
  useQueryToasterErrors(subscriptionQuery);
  const role = query.data;
  const orgs = subscriptionQuery.data || [];
  const currentOrg = orgs.find((o) => o.id === org_id);
  const subscription = currentOrg?.subscription || null;
  const ready = query.isFetched && org.isFetched;
  const feature_flags = org.id ? org.featureFlags : ({} as Org["featureFlags"]);

  const isSuperAdmin = user?.superuser || role === "super";
  const isOrgAdmin = isSuperAdmin || role === "admin";
  return {
    feature_flags,
    isOrgAdmin,
    isSuperAdmin,
    org_id,
    org_name: (org.id && org.name) || "",
    ready,
    role,
    email: (org.id && org.signupEmail) || "",
    user,
    subscription,
  };
} // end useApplicationAuth

function useIsSessionable() {
  const router = useRouter();
  const path = useRouter().asPath;
  const orgId = router.query.orgId;
  return isSessionable(path, orgId as string);
}

function getSessionableType(path: string, orgId: string) {
  const sessionablePrefixes = {
    [TabLink.ASK]: [`/${orgId}/ask`],
    [TabLink.BOARDS]: [`/${orgId}/dashboards`, `/${orgId}/boards`],
    [TabLink.ENTITY]: [`/${orgId}/entity`],
    [TabLink.EXPLORE]: [`/${orgId}/explore`],
    [TabLink.DIGESTS]: [`/${orgId}/topics`],
    [TabLink.NARRATIVES]: [`/${orgId}/narratives`],
  } as const;

  const [linkType] =
    Object.entries(sessionablePrefixes).find(([_, prefixes]) =>
      prefixes.some((prefix) => path.startsWith(prefix))
    ) || [];
  return linkType as unknown as TabLink | undefined;
}

export function isSessionable(path: string, orgId: string) {
  return Boolean(getSessionableType(path, orgId));
}

export type AppTab = {
  current_title: string;
  current_url: string;
  id: string;
  start_title: string;
  start_url: string;
};

export enum TabLink {
  ASK = "5p4RkL3",
  BOARDS = "604rd5",
  ENTITY = "3N71Ty",
  EXPLORE = "3xP10r3",
  DIGESTS = "d1G3sT5",
  NARRATIVES = "n4rR4t1v3",
}

export const ApplicationContext = createContext<
  ReturnType<typeof useApplicationRuntime>
>({} as ReturnType<typeof useApplicationRuntime>);
